import React, { useId, useState } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import Sheet from '../../../common/Sheet';
import type { Medication } from '../../../../dtos/patientmr/medication.dto';
import { MED_ROUTE_OPTIONS, isMedRouteCode, toMedRouteCode } from '../../../../constants/medRoutes';
import { MarFormData, NOT_ON_MED_LIST } from '../types';
import { clock24 } from '../model/entryMeta';

export interface MarMedicationFieldsProps {
  marData: MarFormData;
  setMarData: (next: MarFormData) => void;
  /** Medications offered in the picker (ACTIVE only; ON_HOLD and DISCONTINUED are left out) */
  medications: Medication[];
  disabled?: boolean;
  inputClassName: string;
  labelClassName: string;
  /** Where the dose fields go (between the medication and the route) */
  doseFields?: React.ReactNode;
  /** Phase 18 M7: last GIVEN time per medication id in this visit ("last given 09:40") */
  lastGivenByMedication?: Record<string, string>;
  /** Inline error for the medication (Phase 18 M1) */
  medicationError?: string;
  /** Phase 18U U12: phones and field-mode tablets pick from a searchable list sheet */
  searchable?: boolean;
}

/** Split a stored dose such as "5 mL (milliliters)" into amount + unit */
function splitDose(dose: string): { amount: string; unit: string } {
  const parts = (dose || '').trim().split(/\s+/);
  return { amount: parts[0] || '', unit: parts.slice(1).join(' ') };
}

/** Pick a medication from the list: fills name, dose and route (MED-03). */
export function marDataForMedication(current: MarFormData, med: Medication): MarFormData {
  const { amount, unit } = splitDose(med.dose);
  const route = toMedRouteCode(med.route);
  return {
    ...current,
    medicationId: med.id,
    medicationName: med.name,
    doseAmount: amount,
    doseUnit: unit,
    route,
  };
}

/**
 * MAR medication picker + route select, shared by the full and compact entry forms.
 * "Not on med list" lets the nurse type a name; the server flags that entry (notOnMedList).
 */
const MarMedicationFields: React.FC<MarMedicationFieldsProps> = ({
  marData,
  setMarData,
  medications,
  disabled = false,
  inputClassName,
  labelClassName,
  doseFields,
  lastGivenByMedication = {},
  medicationError,
  searchable = false,
}) => {
  const baseId = useId();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [query, setQuery] = useState('');
  const choose = (value: string) => {
    setPickerOpen(false);
    setQuery('');
    if (value === NOT_ON_MED_LIST) {
      setMarData({ ...marData, medicationId: NOT_ON_MED_LIST, medicationName: '' });
      return;
    }
    const med = medications.find((x) => x.id === value);
    if (med) setMarData(marDataForMedication(marData, med));
  };
  const q = query.trim().toLowerCase();
  const shown = q ? medications.filter((x) => `${x.name} ${x.dose}`.toLowerCase().includes(q)) : medications;
  const picked = medications.find((x) => x.id === marData.medicationId);
  const notOnList = marData.medicationId === NOT_ON_MED_LIST;
  const disabledCls = disabled ? 'opacity-50 cursor-not-allowed bg-gray-100' : '';
  const routeValue = isMedRouteCode(marData.route) ? marData.route : marData.route ? 'OTHER' : '';

  return (
    <>
      <div>
        <label className={labelClassName} htmlFor={`${baseId}-med`}>
          Medication<span className="text-red-600" aria-hidden="true"> *</span>
        </label>
        {searchable ? (
          <button
            id={`${baseId}-med`}
            type="button"
            disabled={disabled}
            onClick={() => setPickerOpen(true)}
            aria-haspopup="dialog"
            aria-describedby={medicationError ? `${baseId}-med-error` : undefined}
            className={`flex w-full items-center justify-between text-left ${inputClassName} ${disabledCls}`}
            data-testid="mar-med-picker"
          >
            <span className={picked || notOnList ? 'text-gray-900' : 'text-gray-500'}>
              {picked ? `${picked.name} — ${picked.dose}` : notOnList ? 'Not on med list' : 'Choose medication…'}
            </span>
            <ChevronDown size={18} aria-hidden="true" />
          </button>
        ) : (
        <select
          id={`${baseId}-med`}
          disabled={disabled}
          className={`w-full ${inputClassName} ${disabledCls}`}
          value={marData.medicationId}
          aria-invalid={Boolean(medicationError) || undefined}
          onChange={(e) => {
            const value = e.target.value;
            if (value === NOT_ON_MED_LIST) {
              setMarData({ ...marData, medicationId: NOT_ON_MED_LIST, medicationName: '' });
              return;
            }
            const med = medications.find((m) => m.id === value);
            if (med) {
              setMarData(marDataForMedication(marData, med));
            } else {
              setMarData({ ...marData, medicationId: '', medicationName: '' });
            }
          }}
        >
          <option value="">Select medication...</option>
          {medications.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name} — {m.dose}{m.prn ? ' (PRN)' : ''}{lastGivenByMedication[m.id] ? ` · last given ${clock24(lastGivenByMedication[m.id])}` : ''}
            </option>
          ))}
          <option value={NOT_ON_MED_LIST}>Not on med list (type the name)</option>
        </select>
        )}
        <Sheet open={pickerOpen} onClose={() => { setPickerOpen(false); setQuery(''); }} title="Choose medication" variant="bottom" testId="mar-med-sheet">
          <div className="space-y-2">
            <label className="relative block">
              <span className="sr-only">Search medications</span>
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" aria-hidden="true" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                placeholder="Search active medications"
                className="min-h-[44px] w-full rounded-lg border border-gray-300 pl-9 pr-3 text-base"
              />
            </label>
            <ul className="divide-y divide-gray-100">
              {shown.map((x) => (
                <li key={x.id}>
                  <button type="button" onClick={() => choose(x.id)} className="flex min-h-[52px] w-full flex-col justify-center px-1 text-left">
                    <span className="text-base font-medium text-gray-900">
                      {x.name}{x.prn && <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-900">PRN</span>}
                    </span>
                    <span className="text-sm text-gray-600">
                      {[x.dose, x.route, x.frequency].filter(Boolean).join(' · ')}
                      {lastGivenByMedication[x.id] ? ` · last given ${clock24(lastGivenByMedication[x.id])}` : ''}
                    </span>
                  </button>
                </li>
              ))}
              {shown.length === 0 && <li className="py-3 text-sm text-gray-600">No active medication matches.</li>}
              <li>
                <button type="button" onClick={() => choose(NOT_ON_MED_LIST)} className="flex min-h-[52px] w-full items-center px-1 text-left text-base font-medium text-amber-900">
                  Not on med list (type the name)
                </button>
              </li>
            </ul>
          </div>
        </Sheet>
        {medicationError && <p id={`${baseId}-med-error`} className="mt-0.5 text-xs text-red-600" data-testid="error-mar-medication">{medicationError}</p>}
        {marData.medicationId && lastGivenByMedication[marData.medicationId] && (
          <p className="mt-0.5 text-xs text-gray-600" data-testid="mar-last-given">
            Last given {clock24(lastGivenByMedication[marData.medicationId])} this visit
          </p>
        )}
        {notOnList && (
          <div className="mt-1 space-y-1">
            <input
              type="text"
              aria-label="Medication name"
              disabled={disabled}
              className={`w-full ${inputClassName} ${disabledCls}`}
              value={marData.medicationName}
              onChange={(e) => setMarData({ ...marData, medicationName: e.target.value })}
              placeholder="Medication name"
            />
            <p className="text-xs text-amber-700" data-testid="mar-not-on-list-note">
              Recorded as not on the medication list. Add it to Medications if it is ordered.
            </p>
          </div>
        )}
      </div>
      {doseFields}
      <div>
        <label className={labelClassName} htmlFor={`${baseId}-route`}>Route</label>
        <select
          id={`${baseId}-route`}
          disabled={disabled}
          className={`w-full ${inputClassName} ${disabledCls}`}
          value={routeValue}
          onChange={(e) => setMarData({ ...marData, route: e.target.value })}
        >
          <option value="">Select route...</option>
          {MED_ROUTE_OPTIONS.map((r) => (
            <option key={r.value} value={r.value}>{r.label}</option>
          ))}
        </select>
      </div>
    </>
  );
};

export default MarMedicationFields;
